import { isLeft } from "@ispb/mop"
import { left, right } from "fp-ts/Either"
import { GenQLPin } from "genql-pin"
import { isNil } from "ramda"
import { cfProcFromHasura } from "shared/cf"
import { maybe } from "shared/maybe"
import { EJSTemplateRenderer } from "shared/old"
import { validateProcedurePrintData } from "shared/procedure-print"

export const EXECUTION_ACTION_FRAGMENT = {
    id: true,
    createdOn: true,
    controlPoint: {
        id: true,
        number: true,
        energySource: {
            prefix: true,
        },
    },
    status: true,
    anonymousName: true,
    anonymousCompanyName: true,
}
export const EXECUTION_FRAGMENT = {
    id: true,
    companyId: true,
    status: true,
    createdOn: true,
    anonymousName: true,
    anonymousCompanyName: true,
    actions: {
        __args: {
            orderBy: [
                {
                    createdOn: "ASC" as const
                }
            ]
        },
        ...EXECUTION_ACTION_FRAGMENT
    }
}
export const PHOTO_FRAGMENT = {
    id: true,
    // name: true,
    type: true,
    present: true,
    //  width: true,
    // height: true,
}
export const PROCEDURE_FRAGMENT = {
    id: true,
    companyId: true,
    createdOn: true,
    updatedOn: true,
    customFields: true,
    type: {
        name: true,
    },
    company: {
        customFields: {
            id: true,
            subject: true,
            name: true,
            type: true,
        },
        settings: {
            defaultTemplate: {
                html: true,
                css: true,
            }
        }
    },
    executions: {
        __args: {
            orderBy: [
                {
                    createdOn: "ASC" as const
                }
            ],
        },
        ...EXECUTION_FRAGMENT
    },
    asset: {
        name: true,
        serial: true,
        barCode: true,
        pin: true,
        manufacturer: true,
        model: true,
        voltage: true,
        ratedAmperage: true,
        customFields: true,
        location: {
            name: true,
            address: true,
            customFields: true,
        },
        type: {
            name: true,
        },
        photo: PHOTO_FRAGMENT
    },
    procedureHazards: {
        hazard: {
            id: true,
            name: true,
            statement: true,
            color: true,
            icon: PHOTO_FRAGMENT,
        }
    },
    controlPoints: {
        __args: {
            orderBy: [
                {
                    step: "ASC" as const
                }
            ]
        },
        id: true,
        step: true,
        number: true,
        energyMagnitude: true,
        barCode: true,
        isolationLocation: true,
        methodOverride: true,
        verificationOverride: true,
        customFields: true,
        updatedOn: true,
        energyMeasure: {
            name: true,
        },
        isolationDevice: {
            name: true,
            methodStatement: true,
            verificationStatement: true,
            icon: PHOTO_FRAGMENT,
            video: PHOTO_FRAGMENT,
        },
        energySource: {
            name: true,
            prefix: true,
            color: true,
            icon: PHOTO_FRAGMENT
        },
        photo: PHOTO_FRAGMENT
    }
}

/*
export function printFromHasura(procedure: ) {
    const asset = maybe(procedure.asset)
    if (asset === undefined) {
        return {
            data: Either.left("This asset does not exist or your pin is incorrect.")
        }
    }
    const printData = {
        asset: {
            name: asset.name,
            serial: asset.serial,
            photo: asset.photo?.id ?? null,
            photoPresent: maybe(asset.photo?.present, _ => _ ? ONE : ZERO, () => null),
            qrCode: asset.barCode,
            pin: asset.pin,
            manufacturer: asset.manufacturer,
            model: asset.model,
            voltage: asset.voltage,
            ratedAmperage: asset.ratedAmperage,
            type: maybe(asset.type, type => {
                return {
                    name: type.name,
                }
            }, () => null),
            location: {
                name: asset.location.name,
                address: asset.location.address,
                customFields: processor.from(asset.location.customFields).toPrint(),
            },
            customFields: processor.from(asset.customFields).toPrint(),
        },
        procedure: {
            createdOn: procedure.createdOn,
            revisedOn: [procedure.updatedOn, ...procedure.controlPoints.map(_ => _.updatedOn)].reduce((max, c) => c > max ? c : max),
            type: procedure.type,
            customFields: processor.from(procedure.customFields).toPrint(),
            hazards: procedure.procedureHazards.map(procedureHazard => {
                const hazard = maybe(procedureHazard.hazard)
                return {
                    name: hazard.name,
                    statement: hazard.statement,
                    color: hazard.color,
                    icon: hazard.icon?.id,
                    iconPresent: maybe(hazard.icon?.present, _ => _ ? ONE : ZERO, () => null),
                }
            }),
            controlPoints: procedure.controlPoints.map(controlPoint => {
                const energyMeasure = controlPoint.energyMeasure
                const isolationDevice = controlPoint.isolationDevice
                return {
                    step: controlPoint.step,
                    number: controlPoint.number,
                    isolationLocation: controlPoint.isolationLocation,
                    energyMagnitude: controlPoint.energyMagnitude,
                    methodStatement: controlPoint.methodOverride ?? isolationDevice.methodStatement,
                    verificationStatement: controlPoint.verificationOverride ?? isolationDevice.verificationStatement,
                    photo: controlPoint.photo?.id ?? null,
                    photoPresent: maybe(controlPoint.photo?.present, _ => _ ? ONE : ZERO, () => null),
                    qrCode: controlPoint.barCode ?? null,
                    customFields: processor.from(controlPoint.customFields).toPrint(),
                    energySource: {
                        ...controlPoint.energySource,
                        icon: controlPoint.energySource.icon.id,
                        iconPresent: controlPoint.energySource.icon.present ? ONE : ZERO,
                    },
                    energyMeasure: energyMeasure,
                    isolationDevice: {
                        ...controlPoint.isolationDevice,
                        icon: controlPoint.isolationDevice.icon.id,
                        iconPresent: controlPoint.isolationDevice.icon.present ? ONE : ZERO,
                        video: controlPoint.isolationDevice.video?.id ?? null,
                        videoPresent: maybe(controlPoint.isolationDevice.video, _ => _.present ? ONE : ZERO, () => null),
                    }
                }
            })
        }
    }
}
*/

export function hasuraProcQuery(barCode: string) {
    return {
        assets: {
            __args: {
                where: { barCode: { _eq: barCode } }
            },
            company: {
                id: true,
            },
            procedures: PROCEDURE_FRAGMENT
        }
    }
}

type XXX = GenQLPin.FieldsSelection<GenQLPin.query_root, ReturnType<typeof hasuraProcQuery>>

export type RawProcedure = XXX["assets"][number]["procedures"][number]
export type RawExecution = RawProcedure["executions"][number]
export type RawExecutionAction = RawExecution["actions"][number]
export type RawExecutionControlPoint = RawExecutionAction["controlPoint"]
export type RawControlPoint = RawProcedure["controlPoints"][number]

export type PublicProcedure = ReturnType<typeof cleanProcedure>
export type PublicExecution = PublicProcedure["executions"][number]
export type PublicExecutionAction = PublicExecution["actions"][number]
export type PublicExecutionControlPoint = PublicProcedure["executions"][number]
export type PublicControlPoint = PublicProcedure["controlPoints"][number]

export function cleanExecutionControlPoint(controlPoint: RawExecutionControlPoint) {
    return {
        ...controlPoint,
        energySource: maybe(controlPoint.energySource)
    }
}
export function cleanControlPoint(controlPoint: RawControlPoint) {
    return {
        ...controlPoint,
        energySource: {
            ...maybe(controlPoint.energySource),
            icon: maybe(controlPoint.energySource?.icon),
        },
        isolationDevice: {
            ...maybe(controlPoint.isolationDevice),
            icon: maybe(controlPoint.isolationDevice?.icon),
        },
        energyMeasure: maybe(controlPoint.energyMeasure)
    }
}
export function cleanExecutionAction(action: RawExecutionAction) {
    return {
        ...action,
        controlPoint: cleanExecutionControlPoint(action.controlPoint)
    }
}
export function cleanExecution(execution: RawExecution) {
    return {
        ...execution,
        actions: execution.actions.map(cleanExecutionAction),
    }
}
export function cleanProcedure(procedure: RawProcedure) {
    const asset = maybe(procedure.asset)
    return {
        ...procedure,
        procedureHazards: procedure.procedureHazards.map(procedureHazard => {
            return {
                ...procedureHazard,
                hazard: maybe(procedureHazard.hazard)
            }
        }),
        executions: procedure.executions.map(cleanExecution),
        controlPoints: procedure.controlPoints.map(cleanControlPoint),
        asset: {
            ...asset,
            photo: asset.photo,
            location: maybe(asset.location)
        }
    }
}

export async function printPublic<T>(procedure: PublicProcedure, uploadsUrl: string, imageUrl: string, parseId: (id: string) => T) {
    if (procedure === undefined) {
        return left("There is no procedure logged for this asset.")
    }
    const asset = procedure.asset
    if (asset === undefined) {
        return left("This asset does not exist or your pin is incorrect.")
    }
    const company = maybe(procedure.company)
    const template = company.settings?.defaultTemplate
    if (isNil(template)) {
        return left("This company does not have a default template set.")
    }
    const processor = cfProcFromHasura(company.customFields, parseId)
    const renderer = new EJSTemplateRenderer(uploadsUrl)
    const printData = {
        asset: {
            name: asset.name,
            serial: asset.serial,
            photo: asset.photo,
            qrCode: asset.barCode,
            pin: asset.pin,
            manufacturer: asset.manufacturer,
            model: asset.model,
            voltage: asset.voltage,
            ratedAmperage: asset.ratedAmperage,
            type: maybe(asset.type, type => {
                return {
                    name: type.name,
                }
            }, () => null),
            location: {
                name: asset.location.name,
                address: asset.location.address,
                customFields: processor.from(asset.location.customFields).toPrint(),
            },
            customFields: processor.from(asset.customFields).toPrint(),
        },
        procedure: {
            createdOn: procedure.createdOn,
            revisedOn: [procedure.updatedOn, ...procedure.controlPoints.map(_ => _.updatedOn)].reduce((max, c) => c > max ? c : max),
            type: procedure.type,
            customFields: processor.from(procedure.customFields).toPrint(),
            hazards: procedure.procedureHazards.map(procedureHazard => {
                const hazard = maybe(procedureHazard.hazard)
                return {
                    name: hazard.name,
                    statement: hazard.statement,
                    color: hazard.color,
                    icon: hazard.icon,
                }
            }),
            controlPoints: procedure.controlPoints.map(controlPoint => {
                const energyMeasure = controlPoint.energyMeasure
                const isolationDevice = controlPoint.isolationDevice
                return {
                    step: controlPoint.step,
                    number: controlPoint.number,
                    isolationLocation: controlPoint.isolationLocation,
                    energyMagnitude: controlPoint.energyMagnitude,
                    methodStatement: controlPoint.methodOverride ?? isolationDevice.methodStatement,
                    verificationStatement: controlPoint.verificationOverride ?? isolationDevice.verificationStatement,
                    photo: controlPoint.photo,
                    qrCode: controlPoint.barCode ?? null,
                    customFields: processor.from(controlPoint.customFields).toPrint(),
                    energySource: controlPoint.energySource,
                    energyMeasure: energyMeasure,
                    isolationDevice: controlPoint.isolationDevice
                }
            })
        }
    }
    const errors = validateProcedurePrintData(printData)
    if (errors.length > 0) {
        return left("This procedure can not be printed. " + errors.join(" "))
    }
    //TODO change to new template system
    const rendered = await renderer.render(procedure.companyId, template.html, template.css, printData)
    if (isLeft(rendered)) {
        return left("This procedure can not be printed. " + rendered.left)
    }
    return right(rendered.right)
}
